import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Wrench, ChevronDown, Minimize2 } from "lucide-react";

interface IntakeDevAutofillProps {
  answers: any;
  setAnswers: (updated: any) => void;
  triggerAutoSave: (updated: any) => void;
  isReadOnly?: boolean;
}

// ── Scenario Generator Logic (formerly in devAutofill.ts) ──

const randomId = () => Math.random().toString(36).substring(2, 11);

const randomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const randomSubset = <T,>(arr: T[], min: number, max: number): T[] => {
  const count = Math.floor(Math.random() * (max - min + 1)) + min;
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const ASSET_CATEGORIES = ["DIGITAL_APPLICATIONS", "INFRASTRUCTURE_NETWORKS", "CLOUD_SAAS_IDENTITY", "CODE_SUPPLY_CHAIN", "TBC"];
const CRITICALITY_LEVELS = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "NOT_SURE"];

const ASSESSMENT_TYPES = {
  DIGITAL_APPLICATIONS: ["WEB_APP", "API", "MOBILE_APP", "AI_LLM"],
  INFRASTRUCTURE_NETWORKS: ["EXTERNAL_NETWORK", "INTERNAL_NETWORK", "VPN_FIREWALL_REVIEW"],
  CLOUD_SAAS_IDENTITY: ["CLOUD_CONFIG", "CLOUD_PENTEST", "M365_REVIEW"],
  CODE_SUPPLY_CHAIN: ["CODE_REVIEW"],
  TBC: []
};

const TARGET_KINDS: Record<string, string> = {
  WEB_APP: "BASE_URL",
  API: "API_BASE_URL",
  MOBILE_APP: "APK_FILE",
  AI_LLM: "ENDPOINT_URL",
  EXTERNAL_NETWORK: "CIDR",
  INTERNAL_NETWORK: "CIDR",
  VPN_FIREWALL_REVIEW: "DEVICE_ID",
  CLOUD_CONFIG: "AWS_ACCOUNT_ID",
  CLOUD_PENTEST: "AWS_ACCOUNT_ID",
  M365_REVIEW: "TENANT_ID",
  CODE_REVIEW: "REPOSITORY_URL"
};

const TESTING_APPROACHES = ["BLACK_BOX", "GREY_BOX", "WHITE_BOX", "TBC"];

const ACCESS_LEVELS = [
  "NO_CREDENTIALS",
  "STANDARD_USER",
  "ADMIN",
  "MULTIPLE_USER_ROLES",
  "API_TOKEN",
  "OAUTH_CLIENT",
  "JWT",
  "MTLS",
  "READ_ONLY_CLOUD_ROLE",
  "SOURCE_CODE_ACCESS",
  "VPN_INTERNAL_NETWORK",
  "CREDENTIALED_READ_ONLY",
  "TBC"
];

const EXPOSURE_TYPES = [
  "INTERNET_FACING",
  "INTERNAL_ONLY",
  "VPN_REQUIRED",
  "CLOUD_ACCOUNT_ACCESS",
  "SOURCE_REPO_ACCESS",
  "MOBILE_BUILD_REQUIRED",
  "TBC"
];

const ENVIRONMENT_STAGES = ["PRODUCTION", "STAGING", "ISOLATED_TEST", "DEVELOPMENT", "TBC"];

const COMPANY_NAMES = [
  "Acme Cyber Technologies",
  "Global Financial Corp",
  "HealthTech Solutions",
  "RetailMax Inc",
  "CloudScale Systems",
  "SecureBank International",
  "DataFlow Analytics",
  "TechShield Services",
  "CyberDefense Pro",
  "NetSecure Enterprise"
];

const CONTACT_NAMES = [
  "Sarah Connor",
  "John Smith",
  "Emily Chen",
  "Michael Brown",
  "Lisa Wang",
  "David Kim",
  "Anna Martinez",
  "James Wilson",
  "Sophie Turner",
  "Robert Taylor"
];

const CLIENT_ROLES = [
  "CEO",
  "Founder",
  "CTO",
  "CISO",
  "Head of Security",
  "Head of Engineering",
  "Compliance Lead",
  "Engineer",
  "Other"
];

const URGENCY_WINDOWS = ["this_week", "two_weeks", "this_month", "this_quarter", "no_deadline", "tbc"];

const TIMELINE_DRIVERS = [
  "customer_pressure",
  "compliance_deadline",
  "board_pressure",
  "post_incident",
  "audit_window",
  "product_launch",
  "curiosity",
  "tbc"
];

const PREVIOUS_TESTING = ["never", "once", "annually", "continuously", "prior_bad"];

const HEADCOUNT_BANDS = ["1_10", "11_50", "51_200", "201_500", "501_2000", "2000_plus"];

const SECURITY_TEAM_SIZES = ["none", "1", "2_5", "6_plus"];

const RELEASE_CADENCES = ["cd", "weekly", "fortnightly", "monthly", "quarterly", "rarely"];

const DATA_SENSITIVITY = ["PII", "PCI", "PHI", "financial", "ip", "public"];

const COMPLIANCE_OVERLAYS = [
  "SOC2",
  "ISO_27001",
  "PCI_DSS",
  "HIPAA",
  "NZISM",
  "GDPR",
  "CUSTOMER_VENDOR_REQUIREMENT",
  "OTHER"
];

const REPORT_AUDIENCES = ["engineering", "executive", "customer", "auditor", "regulator", "procurement"];

const ENGAGEMENT_MODELS = ["ONE_TIME_PENTEST", "CONTINUOUS_PENTEST", "TBC"];

const CONTINUOUS_MODELS = ["continuous_ptaas", "baseline_monthly", "baseline_quarterly", "release_based", "on_demand"];

const BUDGET_SOURCES = [
  "existing_security_budget",
  "compliance_budget",
  "customer_reimbursed",
  "one_off_approval",
  "founder_cashflow",
  "not_sure"
];

const BUYER_AUTHORITIES = [
  "self",
  "cto_approves",
  "security_team",
  "compliance_risk",
  "procurement",
  "executive_board",
  "not_sure"
];

const COMPARING_VENDORS = ["three_plus", "two", "no_referred", "unsure"];

const ASSET_NAMES = [
  "Customer E-Commerce Platform",
  "HQ Operations Directory",
  "Mobile Banking App",
  "AI Customer Service Bot",
  "Cloud Infrastructure",
  "Internal API Gateway",
  "Employee Portal",
  "Payment Processing System",
  "Data Analytics Pipeline",
  "Supply Chain Management",
  "Customer Support Portal",
  "Internal Knowledge Base",
  "HR Management System",
  "Inventory Management",
  "Order Processing System",
  "Marketing Website",
  "Partner Portal",
  "Admin Dashboard",
  "Reporting Platform",
  "Communication Hub",
  "Document Repository",
  "Authentication Service",
  "File Storage System",
  "Backup Infrastructure",
  "Monitoring System",
  "Log Management",
  "Security Operations Center",
  "Threat Detection Platform",
  "Vulnerability Scanner",
  "Compliance Dashboard"
];

const TARGET_VALUES = [
  "https://app.example.com",
  "https://api.example.com/v1",
  "com.example.mobileapp",
  "https://ai.example.com/chat",
  "192.168.1.0/24",
  "10.0.0.0/16",
  "172.16.0.0/12",
  "aws-account-123456789",
  "tenant-id-abc123",
  "https://github.com/example/repo",
  "https://gitlab.com/example/project",
  "device-id-xyz789",
  "https://portal.example.com",
  "https://dashboard.example.com",
  "https://admin.example.com"
];

const generateRandomAsset = (index: number): any => {
  const category = randomItem(ASSET_CATEGORIES.filter(c => c !== "TBC"));
  const assessmentTypes = ASSESSMENT_TYPES[category as keyof typeof ASSESSMENT_TYPES] || [];
  const scopeCount = Math.floor(Math.random() * 3) + 1;
  
  const assessment_scopes = assessmentTypes.slice(0, scopeCount).map((assessmentType) => {
    const targetKind = TARGET_KINDS[assessmentType] || "OTHER";
    const targetValue = randomItem(TARGET_VALUES);
    const testingApproach = randomItem(TESTING_APPROACHES);
    const accessLevels = randomSubset(ACCESS_LEVELS, 1, 3);
    const exposure = randomItem(EXPOSURE_TYPES);
    const environmentStage = randomItem(ENVIRONMENT_STAGES);
    
    return {
      scope_id: `scope-${randomId()}`,
      assessment_type: assessmentType,
      targets: [
        {
          target_id: `target-${randomId()}`,
          target_kind: targetKind,
          target_value: targetValue,
          exposure: exposure,
          environment_stage: environmentStage,
          testing_approach: testingApproach,
          access_levels: accessLevels,
          sizing: {},
          details: {}
        }
      ],
      details: {
        environment: environmentStage,
        notes: `Auto-generated test data for ${assessmentType} testing`
      }
    };
  });

  return {
    asset_id: `asset-${randomId()}`,
    asset_name: ASSET_NAMES[index % ASSET_NAMES.length] + (index >= ASSET_NAMES.length ? ` ${index}` : ""),
    asset_category: category,
    criticality: randomItem(CRITICALITY_LEVELS),
    owner: {
      name: randomItem(CONTACT_NAMES),
      role: randomItem(CLIENT_ROLES)
    },
    assessment_scopes
  };
};

const generateScenario = (scenarioIndex: number): Record<string, any> => {
  const assetCount = Math.floor(Math.random() * 4) + 1;
  const business_assets = Array.from({ length: assetCount }, (_, i) => generateRandomAsset(scenarioIndex * 10 + i));

  return {
    client_name: COMPANY_NAMES[scenarioIndex % COMPANY_NAMES.length] + (scenarioIndex >= COMPANY_NAMES.length ? ` ${scenarioIndex}` : ""),
    client_contact: CONTACT_NAMES[scenarioIndex % CONTACT_NAMES.length],
    client_email: `security${scenarioIndex}@${randomItem(["acme", "example", "test", "demo"])}.com`,
    client_role: randomItem(CLIENT_ROLES),
    
    urgency_window: randomItem(URGENCY_WINDOWS),
    timeline_driver: randomItem(TIMELINE_DRIVERS),
    previous_testing: randomItem(PREVIOUS_TESTING),
    
    headcount_band: randomItem(HEADCOUNT_BANDS),
    security_team_size: randomItem(SECURITY_TEAM_SIZES),
    release_cadence: randomItem(RELEASE_CADENCES),
    
    data_sensitivity: randomSubset(DATA_SENSITIVITY, 2, 4),
    compliance_overlays: randomSubset(COMPLIANCE_OVERLAYS, 1, 3),
    report_audience: randomSubset(REPORT_AUDIENCES, 2, 4),
    
    business_assets,
    
    engagement_model: randomItem(ENGAGEMENT_MODELS),
    continuous_testing_model: randomItem(CONTINUOUS_MODELS),
    
    budget_source: randomItem(BUDGET_SOURCES),
    buyer_authority: randomItem(BUYER_AUTHORITIES),
    comparing_vendors: randomItem(COMPARING_VENDORS),
    
    authorisation_acknowledged: "yes",
    production_risk_acknowledged: "yes",
    emergency_contact: `+1 (555) ${1000 + scenarioIndex} - ${randomItem(CONTACT_NAMES)}`,
    blackout_dates: scenarioIndex % 3 === 0 ? "Weekends only" : "None scheduled",
    
    engagement_objectives: `Auto-generated test scenario ${scenarioIndex + 1} for comprehensive edge case testing`,
    custom_request: scenarioIndex % 2 === 0 ? "Please prioritize high-severity findings" : "Include detailed remediation recommendations"
  };
};

const INTAKE_PRESET_SCENARIOS = [
  { name: "Fintech Platform (Acme Cyber)", index: 0 },
  { name: "Global Enterprise (Global Financial)", index: 1 },
  { name: "Healthcare EHR (HealthTech Solutions)", index: 2 },
  { name: "E-Commerce Core (RetailMax Inc)", index: 3 },
  { name: "SaaS Cloud Infrastructure (CloudScale)", index: 4 },
  { name: "Web3/DeFi Protocol (SecureBank Tech)", index: 5 },
  { name: "AI Analytics Pipeline (DataFlow)", index: 6 },
  { name: "Hardware Drone IoT (TechShield IoT)", index: 7 },
  { name: "EdTech Student Portal (CyberDefense Edu)", index: 8 },
  { name: "Global Logistics Gateway (NetSecure)", index: 9 }
];

// ── React Component ──

const IntakeDevAutofill: React.FC<IntakeDevAutofillProps> = ({
  answers,
  setAnswers,
  triggerAutoSave,
  isReadOnly = false
}) => {
  const isDev = process.env.NODE_ENV === "development" || process.env.REACT_APP_SHOW_DEV_TOOLS === "true";
  const [isExpanded, setIsExpanded] = useState(false);
  const [devPanelPos, setDevPanelPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  
  const dragStartCoords = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragTotalDistance = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    const isGrip = target.closest(".drag-handle");
    const isBubble = !isExpanded && target.closest(".dev-bubble");
    
    if (isGrip || isBubble) {
      setIsDragging(true);
      dragStartCoords.current = { x: e.clientX, y: e.clientY };
      dragOffset.current = {
        x: e.clientX - devPanelPos.x,
        y: e.clientY - devPanelPos.y,
      };
      dragTotalDistance.current = 0;
      target.setPointerCapture(e.pointerId);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const dx = e.clientX - dragStartCoords.current.x;
      const dy = e.clientY - dragStartCoords.current.y;
      dragTotalDistance.current = Math.sqrt(dx * dx + dy * dy);

      setDevPanelPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };

    const handlePointerUp = (e: PointerEvent) => {
      setIsDragging(false);
      try {
        const target = e.target as HTMLElement;
        target.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Safe check
      }

      if (dragTotalDistance.current < 5) {
        setIsExpanded(prev => !prev);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, devPanelPos]);

  if (!isDev || isReadOnly) return null;

  const performAutofill = (scenarioIdx: number) => {
    const scenario = generateScenario(scenarioIdx);
    const updated = {
      ...answers,
      ...scenario
    };
    setAnswers(updated);
    triggerAutoSave(updated);
    toast.success(`⚡ Intake Auto-fill: Loaded test scenario "${scenario.client_name}"!`);
  };

  return (
    <div
      style={{
        transform: `translate(${devPanelPos.x}px, ${devPanelPos.y}px)`,
        touchAction: "none",
      }}
      onPointerDown={handlePointerDown}
      className="fixed bottom-6 right-6 z-[999999] select-none shrink-0"
    >
      {!isExpanded ? (
        /* Collapsed Mini-Bubble FAB */
        <div
          className={`dev-bubble w-9 h-9 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-full flex items-center justify-center cursor-grab shadow-lg border border-indigo-400/30 hover:scale-105 active:scale-95 transition duration-150 relative ${
            isDragging ? "cursor-grabbing ring-2 ring-indigo-500/30" : ""
          }`}
          title="🛠️ Intake Dev Wizard (Click to open, Drag to move)"
        >
          <Wrench size={13} className="text-white animate-pulse" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full ring-1 ring-white" />
        </div>
      ) : (
        /* Sleek, Ultra-Compact open Selector Widget */
        <div
          className={`bg-white/95 backdrop-blur-md border border-indigo-150 p-2 rounded-xl shadow-2xl w-[220px] flex flex-col gap-1.5 transition-all duration-200 ${
            isDragging ? "ring-2 ring-indigo-500/20 shadow-indigo-100/50" : ""
          }`}
        >
          {/* Header Drag Bar */}
          <div className="flex items-center gap-1 pb-1 border-b border-indigo-50/60 select-none shrink-0">
            <div 
              className={`drag-handle text-indigo-300 font-extrabold text-[10px] tracking-tight flex items-center justify-center mr-0.5 select-none ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
              title="Drag to reposition panel"
            >
              ⣿
            </div>
            <div className="flex items-center justify-center w-3.5 h-3.5 rounded bg-indigo-600 text-white shadow-sm shrink-0">
              <Wrench size={8} className="text-white" />
            </div>
            <div className="flex-1">
              <span className="font-extrabold text-indigo-950 text-[8.5px] leading-none block">🛠️ Intake Dev</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              className="w-4.5 h-4.5 p-0.5 flex items-center justify-center rounded text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition shrink-0"
              title="Minimize panel"
            >
              <Minimize2 size={8} />
            </button>
          </div>

          {/* Selector Dropdown */}
          <div className="relative flex flex-col gap-1">
            <select
              defaultValue=""
              onChange={(e) => {
                const idx = e.target.value;
                if (idx !== "") {
                  if (idx === "random") {
                    performAutofill(Math.floor(Math.random() * 30));
                  } else {
                    performAutofill(Number(idx));
                  }
                  e.target.value = "";
                }
              }}
              className="w-full h-6.5 pl-1.5 pr-5 bg-white border border-indigo-150 rounded-lg text-[9px] font-bold text-indigo-900 focus:outline-none focus:border-indigo-500 hover:border-indigo-300 transition cursor-pointer shadow-sm appearance-none"
            >
              <option value="" disabled className="text-indigo-400">-- Choose Scenario (10) --</option>
              {INTAKE_PRESET_SCENARIOS.map((preset, idx) => (
                <option key={idx} value={preset.index}>
                  {idx + 1}. {preset.name}
                </option>
              ))}
              <option value="random" className="font-bold text-indigo-600">🎲 30 Random Scenarios...</option>
            </select>
            <div className="absolute top-2 right-0 pr-1.5 flex items-center pointer-events-none text-indigo-500">
              <ChevronDown size={8} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntakeDevAutofill;
